import React from 'react';
import {
  Users,
  Heart,
  Star,
  Lightbulb,
  Shield,
  Award,
  Target,
  Zap,
  Globe,
  Rocket,
  HeartHandshake,
  BookOpen,
  TrendingUp,
  Eye,
  Compass,
  Gem,
  Flame,
  Crown,
  ThumbsUp,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

/**
 * Map of lowercase icon names to lucide-react components.
 * Used for rendering company value icons stored as text in the DB.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  users: Users,
  heart: Heart,
  star: Star,
  lightbulb: Lightbulb,
  shield: Shield,
  award: Award,
  target: Target,
  zap: Zap,
  globe: Globe,
  rocket: Rocket,
  handshake: HeartHandshake,
  bookopen: BookOpen,
  trendingup: TrendingUp,
  eye: Eye,
  compass: Compass,
  gem: Gem,
  flame: Flame,
  crown: Crown,
  thumbsup: ThumbsUp,
  sparkles: Sparkles,
};

/** Curated list for the icon picker in Settings */
export const ICON_OPTIONS = Object.entries(ICON_MAP).map(([name, Icon]) => ({
  name,
  Icon,
}));

/**
 * Check whether a string is a known lucide icon name rather than an emoji.
 */
export const isLucideIcon = (icon: string): boolean => {
  return icon.length > 2 && ICON_MAP.hasOwnProperty(icon.toLowerCase());
};

/**
 * Render an icon — either as a lucide component (if the value is a known name)
 * or as raw text (emoji).  Falls back to ⭐ if empty.
 */
export const renderValueIcon = (
  icon: string | undefined,
  size: number = 18,
  color?: string
): React.ReactNode => {
  if (!icon) return <Star size={size} color={color} />;

  const key = icon.toLowerCase();
  const LucideComponent = ICON_MAP[key];

  if (LucideComponent) {
    return <LucideComponent size={size} color={color} />;
  }

  // Not a known lucide name — treat as emoji text
  return <span style={{ fontSize: size - 2, lineHeight: 1 }}>{icon}</span>;
};

export default ICON_MAP;
